<template>
  <div>
    <div class="blocks">
      <div class="row pt-3 l-m-b-20 r">
        <div class="col-md-10 d-flex align-items-center">
          <h1 class="pr-3">Claims</h1>
          <div class="input-group search">
            <button type="button" class="btn input-group-btn" @click="clearSearch">
              <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
            </button>
            <input class="form-control search-input" type="text" v-model="q" @keydown="searchText($event)"
                   placeholder="Search"
            />
          </div>
        </div>
        <div class="col-2">
          <load-filter-dropdown
            @filterSelected="loadFilters"
            @filterTemplatesInitialized="setFilterTemplatesInitialized"
            :route="$route.name"
          />
        </div>
      </div>
      <div class="row search-wrap">
        <div class="col-11 filters-field pl-0">
          <span>Filters:</span>
          <span v-if="filtersLoaded">
            <span v-if="filters.company.length && companies">
              <span v-for="company in filters.company" :key="company" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('company', company)" />
                Company: {{ companies.find(el => el.id === company).name }}</span>
            </span>
            <span v-if="filters.carrier.length && carriers">
              <span v-for="carrier in filters.carrier" :key="carrier" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('carrier', carrier)" />
                Carrier: {{ carriers.find(el => el.id === carrier).name }}</span>
            </span>
            <span v-if="filters.claimType.length && claimTypes">
              <span v-for="claimType in filters.claimType" :key="claimType" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('claimType', claimType)" />
                Claim type: {{ claimTypes.find(el => el.id === claimType).name }}</span>
            </span>
            <span v-if="filters.claimCreatedDate" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('claimCreatedDate')" />
              Created At: {{ filters.claimCreatedDate }}</span>
            <span v-if="filters.clusterUser.length">
              <span v-for="clusterUser in filters.clusterUser" :key="clusterUser" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('clusterUser', clusterUser)" />
                Claim agent: {{ claimsFilter.find(el => el.id === clusterUser).full_name }}</span>
            </span>
            <span v-if="filters.claimStatusId.length">
              <span v-for="claimStatusId in filters.claimStatusId" :key="claimStatusId" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('claimStatusId', claimStatusId)" />
                Status: {{ claimStatuses.find(el => el.id === claimStatusId).name }}</span>
            </span>
            <span v-if="filters.tags && filters.tags.length && tags">
              <span v-for="tag in filters.tags" :key="tag" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('tags', tag)" />
                Tag: {{ tags.find(el => el.id === tag).name }} </span>
            </span>
          </span>
          <button type="button" class="btn btn-secondary add-filters" @click="showFilters = true">
            <i class="fa fa-plus filter-icon" />
          </button>
        </div>
        <div class="col-1 d-flex justify-content-end pr-0">
          <button type="button" class="btn btn-secondary expand-all" @click="toggleAllClaims">
            <i :class="[claims && expandedAll ? 'fa fa-compress' : 'fa fa-expand']" />
          </button>
        </div>
      </div>
    </div>
    <template v-if="!claims">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <div class="blocks">
        <ClaimBlock
          v-for="(claim, index) in claims" :key="index"
          :claim="claim"
          :expanded="expanded"
          :claims-resolutions="claimResolutions"
          @expanded="toggleClaim"
          @update="getClaims"
        />
      </div>

      <div v-if="claims && !claims.length" class="claims">
        No claims added.
      </div>

      <div class="row blocks">
        <div class="col-md-6 mb-1 text-black-50"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-md-6 mb-1" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showFilters" @click.self="showFilters = false">
        <div class="filters-side-modal flex flex-column">
          <div @click="showFilters = !showFilters" class="pointer">
            <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Add Filter</h1>
          </div>
          <div>
            <p>
              Filters are applied automatically. You can remove them one by one with minus button or with clear filters button.
            </p>
          </div>
          <div class="l-bg-white">
            <div class="row">
              <div class="col">
                <div class="row filters">
                  <div class="col-12" v-if="companies && companies.length">
                    <div class="select-wrap">
                      <label>Company</label>
                      <key-multiselect v-model="filters.company"
                                       :options="companies"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12" v-if="carriers && carriers.length">
                    <div class="select-wrap">
                      <label>Carriers</label>
                      <key-multiselect v-model="filters.carrier"
                                       :options="carriers"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :id="'carrier-select'"
                                       :placeholder="'Select Carrier'"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12" v-if="claimTypes && claimTypes.length">
                    <div class="select-wrap">
                      <label>Claim types</label>
                      <key-multiselect v-model="filters.claimType"
                                       :options="claimTypes"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Claim created date</label>
                      <flat-pickr v-model="filters.claimCreatedDate"
                                  :config="config"
                                  class="simple-select-input"
                                  placeholder="Select Created Date"
                                  name="date"
                                  @on-close="onCreatedDateChange"
                      />
                      <span class="position-absolute clear-date" @click="clearCreatedDateAndSearch"
                            v-if="filters.claimCreatedDate"
                      >
                        <span class="mod-icon-clear" />
                        <span aria-hidden="true" class="sr-only">Clear</span>
                      </span>
                      <span class="position-absolute clear-date clear-date-transparent" v-else><span
                        class="mod-icon-rates-dark"
                      /></span>
                    </div>
                  </div>
                  <div class="col-12" v-if="claimsFilter && claimsFilter.length">
                    <div class="select-wrap">
                      <label>Claim agent</label>
                      <key-multiselect v-model="filters.clusterUser"
                                       :options="claimsFilter"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :id="'sales-claim-agent'"
                                       :placeholder="'Select Claim agent'"
                                       :preserve-search="false"
                                       label="full_name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.full_name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Status</label>
                      <key-multiselect v-model="filters.claimStatusId"
                                       :options="claimStatuses"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div v-if="$_can('tags.show')" class="col-12">
                    <div class="select-wrap">
                      <label>Tags</label>
                      <key-multiselect
                        v-model="filters.tags"
                        :options="tags"
                        :multiple="true"
                        :clear-on-select="false"
                        :close-on-select="false"
                        track-by="id"
                        @input="search"
                        label="name"
                        class="w-100"
                      >
                        <template slot="tag" slot-scope="props">
                          <span class="multiselect__tag p-0 bg-transparent">
                            <span>
                              <tag-color
                                :color="props.option.color.name"
                                :name="props.option.name"
                              />
                            </span>
                          </span>
                        </template>
                        <template slot="option" slot-scope="props">
                          <div class="option__desc">
                            <div class="d-flex align-items-center justify-content-between">
                              <span class="option__title">
                                {{ props.option.name }}
                              </span>
                            </div>
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-auto">
            <div class="col">
              <button type="button" class="btn btn-not-active text-dark w-100" @click="clearFilters"
                      v-if="!clearFiltersButtonShouldBeDisabled"
              >
                Clear filters
              </button>
            </div>
            <div class="col">
              <button type="button" class="btn btn-primary text-white w-100" @click="saveFiltersModal = true"
                      v-if="!clearFiltersButtonShouldBeDisabled"
              >
                Save filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Save filters modal -->
    <SaveFiltersModal
      v-if="saveFiltersModal"
      @close="saveFiltersModal = false"
      :filters="filters"
      :route="$route.path"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import axios from 'axios';
import ClaimBlock from './ClaimBlock';
import Pagination from '../Pagination';
import TagColor from '../settings/Tags/TagColor';
import SaveFiltersModal from '../SaveFiltersModal.vue';
import LoadFilterDropdown from '../LoadFilterDropdown.vue';

export default {
  name: 'Claims',
  components: {
    LoadFilterDropdown,
    SaveFiltersModal,
    Pagination,
    ClaimBlock,
    flatPickr,
    TagColor,
  },
  data: () => ({
    config: {
      dateFormat: 'm/j/Y',
      mode: 'range',
    },
    expanded: [],
    expandedAll: false,
    filters: {
      company: [],
      carrier: [],
      claimType: [],
      claimCreatedDate: '',
      createdDateStart: '',
      createdDateEnd: '',
      clusterUser: [],
      claimStatusId: [],
      tags: [],
    },
    filterTemplatesInitialized: false,
    hasDefaultFilterTemplate: true,
    page: 1,
    q: '',
    saveFiltersModal: false,
    showFilters: false,
    tags: [],
  }),
  computed: {
    ...mapState({
      carriers: state => state.carriers.carriers.data,
      claims: state => state.claims.claims.data,
      meta: state => state.claims.claims.meta,
      companies: state => state.companies.companies.data,
      claimTypes: state => state.claims.claimTypes.data,
      claimStatuses: state => state.values.claimStatuses,
      claimsFilter: state => state.clusterUsers.claimsFilter,
      claimResolutions: state => state.claims.claimResolutions.data,
      filtersLoaded: state => state.guard.filtersLoaded,
    }),
    ...mapGetters(['getClaimsFiltered']),
    clearFiltersButtonShouldBeDisabled() {
      return (
        !this.filters.company.length &&
        !this.filters.carrier.length &&
        !this.filters.claimType.length &&
        !this.filters.claimCreatedDate &&
        !this.filters.clusterUser.length &&
        !this.filters.claimStatusId.length &&
        !this.filters.tags
      );
    },
  },
  watch: {
    filterTemplatesInitialized() {
      if (!this.hasDefaultFilterTemplate) {
        if (Object.keys(this.$route.query).length !== 0) {
          this.setFilters(this.$route.query);
        }
      }
      this.filter();
    },
    claims() {
      if (this.expandedAll) {
        this.claims.forEach(claim => {
          this.expanded.push(claim.id);
        });
      } else {
        this.expanded = [];
      }
    },
  },
  mounted() {
    this.getTags();
    this.getClaimTypes();
    this.getClaimStatuses();
    this.getClaimResolutions();
  },
  methods: {
    ...mapMutations(['setClaims']),
    ...mapActions(['getClaims', 'getClaimStatuses', 'getClaimResolutions', 'getClaimTypes']),
    getTags() {
      axios
        .get('/companies/tags/list?paginated=false')
        .then(res => {
          this.tags = res.data.data;
        })
        .catch(() => {});
    },
    loadFilters(filtersData) {
      this.filters = Object.assign(filtersData);
      this.filter();
    },
    setFilterTemplatesInitialized(hasDefault) {
      this.hasDefaultFilterTemplate = hasDefault;
      this.filterTemplatesInitialized = true;
    },
    setFilters(query) {
      this.filters = Object.assign(
        {
          company: query.company_id ? query.company_id.split(',').map(i => Number(i)) : [],
          carrier: query.carrier_id ? query.carrier_id.split(',').map(i => Number(i)) : [],
          createdDateStart: query.created_from.toString(),
          createdDateEnd: query.created_to.toString(),
          claimCreatedDate:
            query.created_from && query.created_to
              ? `${query.created_from.toString()} to ${query.created_from.toString()}`
              : '',
          clusterUser: query.cluster_user_id
            ? query.cluster_user_id.split(',').map(i => Number(i))
            : [],
          claimStatusId: query.claim_status_id
            ? query.claim_status_id.split(',').map(i => Number(i))
            : [],
          claimType: query.claim_type_id ? query.claim_type_id.split(',').map(i => Number(i)) : [],
          sortKey: query.sort,
          sortOrders: query.order,
          tags: query.tag_id ? query.tag_id.split(',').map(i => Number(i)) : [],
        },
        {},
      );
      this.page = query.page;
      this.q = query.q.toString();
    },
    view(event, id) {
      this.$router.push(`/claims/edit/${id}${this.q ? `?q=${encodeURIComponent(this.q)}` : ''}`);
    },
    toggleClaim(value) {
      if (this.expanded.includes(value)) {
        const index = this.expanded.indexOf(value);
        if (index > -1) {
          this.expanded.splice(index, 1);
        }
      } else {
        this.expanded.push(value);
      }
    },
    toggleAllClaims() {
      if (!this.expanded.length) {
        this.claims.forEach(claim => {
          this.expanded.push(claim.id);
          this.expandedAll = true;
        });
      } else {
        this.expanded = [];
        this.expandedAll = false;
      }
    },
    changePage(pageNum) {
      this.setClaims({});
      this.page = pageNum;
      this.filter();
    },
    searchText(e) {
      // eslint-disable-next-line no-unused-expressions
      e.key;
      this.page = 1;
      const vm = this;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        vm.filter();
      }, 500);
      // if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
      //   const vm = this;
      //   clearTimeout(this.timer);
      //   this.timer = setTimeout(() => {
      //     vm.filter();
      //   }, 500);
      // }
    },
    search() {
      this.page = 1;
      this.setClaims({});
      this.filter();
    },
    filter() {
      const query =
        `page=${this.page}&company_id=${this.filters.company}` +
        `&carrier_id=${this.filters.carrier}` +
        `&claim_type_id=${this.filters.claimType}` +
        `&cluster_user_id=${this.filters.clusterUser}` +
        `&created_from=${this.filters.createdDateStart}&created_to=${this.filters.createdDateEnd}` +
        `&claim_status_id=${this.filters.claimStatusId}` +
        `&q=${encodeURIComponent(this.q)}&tag_id=${this.filters.tags}`;
      this.getClaimsFiltered(query);
      if (this.$_checkProperties(this.filters)) {
        this.$router.push(`/claims?${query}`);
      } else {
        this.$router.push(`/claims`);
      }
    },
    removeFilter(model, id) {
      if (id) {
        this.filters[model] = this.filters[model].filter(element => element !== id);
      } else {
        this.filters[model] = '';
      }
      if (model === 'claim_created_date') {
        this.filters.createdDateStart = '';
        this.filters.createdDateEnd = '';
      }
      this.search();
    },
    onCreatedDateChange(selectedDates) {
      this.filters.createdDateStart = selectedDates[0]
        ? selectedDates[0].toLocaleDateString('en-US')
        : '';
      this.filters.createdDateEnd = selectedDates[1]
        ? selectedDates[1].toLocaleDateString('en-US')
        : '';
      this.search();
    },
    clearCreatedDateAndSearch() {
      this.filters.claimCreatedDate = '';
      this.filters.createdDateStart = '';
      this.filters.createdDateEnd = '';
      this.page = 1;
      this.search();
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          carrier: [],
          claimCreatedDate: '',
          claimStatusId: [],
          claimType: [],
          clusterUser: [],
          company: [],
          createdDateEnd: '',
          createdDateStart: '',
          tags: [],
        },
        {},
      );
      this.setClaims({});
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
  },
};
</script>
<style lang="scss" scoped>
.input-group.search {
  width: 215px;
}
</style>



// WEBPACK FOOTER //
// src/components/claims/Claims.vue